<template>
  <div>
    <div class="row mb-1">
      <div class="col">
        <input
          class="form-check-input mr-2"
          type="checkbox"
          id="topSpenderCampaign"
          v-model="form.topSpenderCampaign"
          :disabled="isDisabled"
        />
        <label class="form-check-label" for="topSpenderCampaign">
          Enable spending condition
        </label>
      </div>
    </div>
    <template v-if="form.topSpenderCampaign">
      <hr class="mb-3"/>
      <div class="row">
        <div class="col-6">
          <label for="campaignStartDate">Spending period start date</label>
          <DatePicker
            id="campaignStartDate"
            v-bind="$attrs"
            v-model="form.campaignPurchaseCalculationStartDate"
            format="D MMMM yyyy"
            lang="en"
            :value="form.campaignPurchaseCalculationStartDate"
            @change="
              changeCampaignStartDate($event);
              form.campaignPurchaseCalculationStartDate = $event;
            "
            style="margin-top: -0.6rem"
          />
          <div
            v-if="isSubmit && error.errorCampaignPurchaseCalculationStartDate"
            class="text-error"
          >
            {{ error.errorCampaignPurchaseCalculationStartDate.message }}
          </div>
        </div>
        <div class="col-6">
          <label for="campaignEndDate">Spending period end date</label>
          <DatePicker
            id="campaignEndDate"
            v-bind="$attrs"
            v-model="form.campaignPurchaseCalculationEndDate"
            format="D MMMM yyyy"
            lang="en"
            :value="form.campaignPurchaseCalculationEndDate"
            :minDate="form.campaignPurchaseCalculationStartDate"
            @change="form.campaignPurchaseCalculationEndDate = $event"
            style="margin-top: -0.6rem"
          />
          <div
            v-if="isSubmit && error.errorCampaignPurchaseCalculationEndDate"
            class="text-error"
          >
            {{ error.errorCampaignPurchaseCalculationEndDate.message }}
          </div>
        </div>
      </div>

      <div class="row mt-4">
        <div class="col-6">
          <label for="campaignMinimumSpending">Minimum spending (THB)</label>
          <NumberInput
            v-model.number="form.campaignMinimumSpending"
            input-id="campaignMinimumSpending"
            :custom-style="{ marginTop: '3px' }"
            :is-decimal-allowed="true"
          />
          <div
            v-if="isSubmit && error.errorCampaignMinimumSpending"
            class="text-error"
          >
            {{ error.errorCampaignMinimumSpending.message }}
          </div>
        </div>
      </div>

      <div class="row mt-4">
        <div class="col-12">
          <label for="bannerFileInput">
            Campaign banner image
            <span class="text-muted">(Optional)</span>
          </label>
          <div class="campaign-banner-upload" @click="triggerBannerUpload">
            <input
              type="file"
              id="bannerFileInput"
              ref="bannerFileInput"
              style="display: none"
              accept="image/png,image/jpeg"
              @change="uploadCampaignBanner"
            />
            <div v-if="!previewCampaignBannerUrl" class="banner-placeholder">
              <img
                src="~assets/images/upload-icon.png"
                alt="Upload"
                class="upload-icon"
              />
              <div class="mt-2">Click to upload banner image</div>
              <div class="upload-hint">PNG, JPG up to 10MB</div>
            </div>
            <img
              v-else
              :src="previewCampaignBannerUrl"
              alt="Campaign Banner"
              class="banner-preview-img"
            />
          </div>
          <div v-if="error.errorCampaignBanner" class="text-error">
            {{ error.errorCampaignBanner.message }}
          </div>
        </div>
      </div>
      <div class="row">
        <div class="offset-10 col-2">
          <label for="selectedLanguage">Language</label>
          <multiselect
            id="selectedLanguage"
            v-model="langSelect"
            :options="[{ text: 'TH' }, { text: 'EN' }]"
            label="text"
            track-by="text"
          >
          </multiselect>
        </div>
      </div>
      <div class="row mt-4">
        <div class="col-12">
          <label :for="'campaignDetail_' + langSelect.text"
          >Campaign details ({{ langSelect.text }})</label
          >
          <div v-show="langSelect.text === 'TH'">
            <CkEditor
              id="campaignDetail_TH"
              :value="form.campaignDetailTh"
              :isTh="true"
              @setContent="setCampaignDetail"
            ></CkEditor>
            <div
              v-if="isSubmit && error.errorCampaignDetailTh"
              class="text-error"
            >
              {{ error.errorCampaignDetailTh.message }}
            </div>
          </div>
          <div v-show="langSelect.text === 'EN'">
            <CkEditor
              id="campaignDetail_EN"
              :value="form.campaignDetailEn"
              :isTh="false"
              @setContent="setCampaignDetail"
            ></CkEditor>
            <div
              v-if="isSubmit && error.errorCampaignDetailEn"
              class="text-error"
            >
              {{ error.errorCampaignDetailEn.message }}
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import CkEditor from "components/CkEditor/CkEditor";
import DatePicker from "components/Datepicker/Datepicker.vue";
import NumberInput from "components/NumberInput/NumberInput.vue";

export default {
  name: "TopSpenderCampaignSection",
  components: {CkEditor, DatePicker, NumberInput},
  props: {
    form: {
      type: Object,
      required: true,
    },
    isSubmit: {
      type: Boolean,
      default: false,
    },
    error: {
      type: Object,
      default: () => ({}),
    },
    previewCampaignBannerUrl: {
      type: String,
      default: null,
    },
    uploadCampaignBanner: {
      type: Function,
      required: true,
    },
    isDisabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      langSelect: {text: "TH"},
    };
  },
  methods: {
    triggerBannerUpload() {
      this.$refs.bannerFileInput.click();
    },
    changeCampaignStartDate(val) {
      if (val > this.form.campaignPurchaseCalculationEndDate) {
        this.form.campaignPurchaseCalculationEndDate = null;
      }
    },
    setCampaignDetail(content, isTh = true) {
      if (isTh) {
        this.form.campaignDetailTh = content || "";
      } else {
        this.form.campaignDetailEn = content || "";
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.campaign-banner-upload {
  border: 2px dashed #ccc;
  border-radius: 8px;
  padding: 30px;
  text-align: center;
  cursor: pointer;

  &:hover {
    border-color: #999;
  }
}

.banner-placeholder {
  color: #6c757d;

  .upload-icon {
    width: 4rem;
    height: 4rem;
  }

  .upload-hint {
    font-size: 0.85rem;
    color: #9ca3af;
    margin-top: 4px;
  }
}

.banner-preview-img {
  max-width: 100%;
  max-height: 300px;
  object-fit: contain;
}
</style>